// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The thumbnail is drawn **here, in the browser**, because the bytes no longer pass through our
 * server — it uploads straight to S3 and never sees the picture.
 *
 * It costs the person nothing noticeable: a canvas resize of a photo they already hold in memory,
 * against a round trip they would otherwise wait through. The output matches what `sharp` produced
 * before, so a file uploaded either way looks the same in the grid.
 *
 * `null` for anything that is not an image, or that the browser cannot decode — the drive falls back
 * to the category icon, which is what it does for videos and documents anyway.
 */
export async function makeThumbnail(file: File): Promise<Blob | null> {
    if (!file.type.startsWith("image/")) return null;

    try {
        // `createImageBitmap` honours EXIF orientation, so phone photos do not come out sideways.
        const source = await createImageBitmap(file, { imageOrientation: "from-image" });

        const { maxSize, quality, mimeType } = constants.thumbnail;
        const scale = Math.min(1, maxSize / Math.max(source.width, source.height));
        const width = Math.max(1, Math.round(source.width * scale));
        const height = Math.max(1, Math.round(source.height * scale));

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");
        if (!context) return null;
        context.drawImage(source, 0, 0, width, height);
        source.close();

        return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), mimeType, quality));
    } catch {
        // A corrupt or exotic image is not a failed upload. The file stands, the grid shows an icon.
        return null;
    }
}
